import React from 'react'
import PropTypes from 'prop-types'
import { MeTable } from '@CommonComponents'
import { Button } from '@CommonComponents/buttons'
import { toast } from '@CommonComponents/toast'
import {
  ROW_KEY,
  drilldownsFor,
  isPlainObject,
  listColumns,
  memberPath,
  toTableRows,
} from './explorer'

const codeStyle = {
  maxHeight: '560px',
  overflow: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  background: 'var(--surface-subtle)',
  border: '1px solid var(--border)',
  color: 'var(--text)',
  padding: '12px',
  marginBottom: 0,
}

const copy = text => {
  navigator.clipboard
    .writeText(text)
    .then(() => toast.success('Copied'))
    .catch(() => toast.error('Could not copy to the clipboard'))
}

const json = value => JSON.stringify(value, null, 2)

export const CodeBlock = ({ text, label }) => (
  <div className="mb-3">
    <div className="d-flex align-items-center justify-content-between mb-2">
      <strong>{label}</strong>
      <Button
        className="me-btn me-btn-ghost me-btn-xs"
        icon="fa-regular fa-copy"
        onClick={() => copy(text)}
      >
        Copy
      </Button>
    </div>
    <pre style={codeStyle}>{text}</pre>
  </div>
)

CodeBlock.propTypes = {
  text: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
}

const ListResult = ({ step, rows, onDrill, onRawPath }) => {
  const columns = listColumns(step.action, rows)
  const config = {
    tableId: `canvas-api-explorer-${step.id}`,
    title: `${rows.length} ${rows.length === 1 ? 'result' : 'results'}`,
    headingLevel: 3,
    caption: step.label,
    rowKey: ROW_KEY,
    columns: columns.map((key, i) => ({
      key,
      label: key,
      type: 'text',
      sortable: true,
      searchable: true,
      required: i === 0,
      wrap: 'ellipsis',
    })),
    defaultSort: { key: columns[0], direction: 'ascending' },
    pageSizes: [25, 50, 100],
    defaultPageSize: 25,
    source: { mode: 'client', max: 100000 },
    searchLabel: 'Search results',
    searchPlaceholder: 'Search these results',
    emptyState: { title: 'Canvas returned an empty list' },
  }

  // registered actions offer their drilldowns; custom requests offer GET <path>/<id>
  const rowActions = tableRow => {
    const row = tableRow.__raw
    if (step.action) {
      return drilldownsFor(step.action, row).map(drill => ({
        key: drill.action,
        label: drill.label,
        icon: 'fa-arrow-right',
        onSelect: () => onDrill(drill, row),
      }))
    }
    const path = memberPath(step.path, row)
    return path
      ? [
          {
            key: 'member',
            label: `GET ${path}`,
            icon: 'fa-arrow-right',
            onSelect: () => onRawPath(path),
          },
        ]
      : []
  }

  return (
    <MeTable
      key={step.id}
      config={config}
      rows={toTableRows(rows, columns)}
      status="ready"
      rowActions={rowActions}
      renderDetail={tableRow => <pre style={codeStyle}>{json(tableRow.__raw)}</pre>}
    />
  )
}

ListResult.propTypes = {
  step: PropTypes.object.isRequired,
  rows: PropTypes.array.isRequired,
  onDrill: PropTypes.func.isRequired,
  onRawPath: PropTypes.func.isRequired,
}

const ObjectResult = ({ step, data, onDrill }) => {
  const drills = step.action ? drilldownsFor(step.action, data) : []
  const htmlField = step.action && step.action.html_field
  const html = htmlField && typeof data[htmlField] === 'string' ? data[htmlField] : null
  const rest = html === null ? data : { ...data, [htmlField]: '(shown above)' }
  return (
    <>
      {drills.length > 0 && (
        <div className="d-flex flex-wrap mb-3" style={{ gap: '8px' }}>
          {drills.map(drill => (
            <Button
              key={drill.action}
              className="me-btn me-btn-secondary me-btn-sm"
              icon="fa-regular fa-arrow-right"
              onClick={() => onDrill(drill, data)}
            >
              {drill.label}
            </Button>
          ))}
        </div>
      )}
      {html !== null && <CodeBlock label="Raw HTML" text={html} />}
      <CodeBlock label={html !== null ? 'Other fields' : 'Response'} text={json(rest)} />
    </>
  )
}

ObjectResult.propTypes = {
  step: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  onDrill: PropTypes.func.isRequired,
}

// The error message itself is in the card's alert band (ApiExplorer); this is what Canvas sent.
const ErrorResult = ({ error }) =>
  error.canvasBody === undefined ? null : (
    <CodeBlock
      label={`Canvas response (HTTP ${error.canvasStatus})`}
      text={typeof error.canvasBody === 'string' ? error.canvasBody : json(error.canvasBody)}
    />
  )

ErrorResult.propTypes = {
  error: PropTypes.object.isRequired,
}

const ResultView = ({ step, onDrill, onRawPath }) => {
  if (step.status === 'loading') return <p className="text-muted">Calling Canvas…</p>
  if (step.status === 'error') return <ErrorResult error={step.error} />

  const { contentType, data } = step.response
  if (contentType === 'text') return <CodeBlock label="Response (not JSON)" text={data} />
  if (Array.isArray(data)) {
    return <ListResult step={step} rows={data} onDrill={onDrill} onRawPath={onRawPath} />
  }
  if (isPlainObject(data)) return <ObjectResult step={step} data={data} onDrill={onDrill} />
  return <CodeBlock label="Response" text={json(data)} />
}

ResultView.propTypes = {
  step: PropTypes.object.isRequired,
  onDrill: PropTypes.func.isRequired,
  onRawPath: PropTypes.func.isRequired,
}

export default ResultView
